import { Link } from "react-router-dom";
import { defineListView } from "@/base/list/defineListView";
import type { ListFilterOption, ListFilterSource } from "@/base/list/types";
import type { AuthedBlobRequester, AuthedRequester } from "@/lib/brokerAuth";
import {
  downloadAccountDocument,
  listAccountDocumentsPage,
  type AccountDocumentFacets,
  type AccountDocumentRow,
} from "@/lib/brokerAccounts";
import {
  documentOriginLabel,
  documentRelatedLabel,
  documentVersionLabel,
} from "@/lib/accountDocumentList";
import { formatShortDate } from "@/lib/listFormat";
import { DocumentDownload } from "@/components/surety-account/DocumentDownload";

// BRK-S410, BRK-S440 — the Surety account's Documents tab as a BaseListView
// definition, embedded in the profile (BRK-S255: every list is one), in the
// October wireframe's seven columns: Document ID · Document · Type · Related
// to · Version · Added · Download.
//
// PMS DOES THE WORK. The aggregated read (`GET /accounts/{id}/documents
// ?scope=all`, PMS-S843) takes the year, type, related-to, search and
// paging parameters, and serves each row's number, its submission's id (the
// SB link) and whether it is the latest version. The filters' choices are the
// read's own `facets`, over the whole set whatever the request narrowed.
//
// URL keys of its own (`docYear`, `docType`, `docRelated`, `docQ`, `docPage`):
// the profile holds its tab in `?tab=`, Billing its list under `bill*` and Bond
// history under `bh*`.
//
// No row link: a document has no page of its own, so the name is text and
// Preview / Download sit in the cells.

const PILL =
  "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
const WRAP = "[overflow-wrap:anywhere]";

export function accountDocumentsListView({
  request,
  fetchBlob,
  accountId,
  onPreview,
}: {
  request: AuthedRequester;
  /** The authenticated file read, for each row's Download. */
  fetchBlob: AuthedBlobRequester;
  accountId: string;
  /** Opens the Preview dialog on a row. */
  onPreview: (row: AccountDocumentRow) => void;
}) {
  // The three filters' choices come from ONE read of the facets. A failed
  // read is forgotten, so the next mount asks again.
  let facets: Promise<AccountDocumentFacets> | null = null;
  function readFacets(): Promise<AccountDocumentFacets> {
    if (facets) return facets;
    const read = listAccountDocumentsPage(request, accountId, {
      limit: 1,
      offset: 0,
    }).then((page) => page.facets);
    facets = read;
    read.catch(() => {
      if (facets === read) facets = null;
    });
    return read;
  }

  const endpoint = `/api/broker/accounts/${accountId}/documents` as const;
  const served = (
    pick: (f: AccountDocumentFacets) => readonly ListFilterOption[],
  ): ListFilterSource => ({ endpoint, load: () => readFacets().then(pick) });

  return defineListView<AccountDocumentRow>({
    endpoint,
    noun: "documents",
    title: "Documents",
    wide: true,
    columns: [
      {
        // PMS's number for it ("BND-10049") — never one built here.
        id: "id",
        header: "Document ID",
        kind: "mono",
        cell: (r) => (
          <span className={`text-xs tabular-nums ${WRAP}`}>
            {r.documentNumber}
          </span>
        ),
      },
      {
        id: "document",
        header: "Document",
        kind: "title",
        cell: (r) => <span className={WRAP}>{r.filename}</span>,
        subtitle: (r) => (
          <span className="flex flex-wrap items-center gap-x-2">
            <span>{documentOriginLabel(r.source)}</span>
            <button
              type="button"
              onClick={() => onPreview(r)}
              aria-label={`Preview ${r.filename}`}
              className="relative z-[1] font-medium text-brk-accent hover:underline"
            >
              Preview
            </button>
          </span>
        ),
      },
      {
        id: "type",
        header: "Type",
        cell: (r) => (
          <span className={`${PILL} bg-brk-bg text-brk-text`}>
            {r.typeLabel}
          </span>
        ),
      },
      {
        id: "related",
        header: "Related to",
        cell: (r) =>
          r.relatedTo ? (
            <Link
              to={`/surety/submissions/${encodeURIComponent(r.relatedTo.submissionId)}`}
              className="relative z-[1] whitespace-nowrap text-brk-accent hover:underline"
            >
              {r.relatedTo.sbNumber}
            </Link>
          ) : (
            <span className="whitespace-nowrap">{documentRelatedLabel(r)}</span>
          ),
      },
      {
        id: "version",
        header: "Version",
        cell: (r) => {
          const version = documentVersionLabel(r);
          return version === null ? (
            <span aria-label="No version">—</span>
          ) : (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <span className="tabular-nums">{version}</span>
              {r.latest && (
                <span className={`${PILL} bg-brk-accent/10 text-brk-accent`}>
                  Latest
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: "added",
        header: "Added",
        cell: (r) => (
          <span className="tabular-nums">{formatShortDate(r.createdAt)}</span>
        ),
      },
      {
        id: "download",
        header: "Download",
        cell: (r) => (
          <DocumentDownload
            label={`Download ${r.filename}`}
            fallbackName={r.filename}
            download={() => downloadAccountDocument(fetchBlob, accountId, r.id)}
          />
        ),
      },
    ],
    rowKey: (r) => r.id,
    rowHref: () => null,
    rowLabel: (r) => r.filename,
    search: {
      urlKey: "docQ",
      apiParam: "q",
      label: "Search documents",
      placeholder: "ID or document name",
    },
    filters: [
      {
        urlKey: "docYear",
        apiParam: "year",
        label: "Year",
        allLabel: "All years",
        options: served((f) => f.years.map((y) => ({ value: y, label: y }))),
      },
      {
        urlKey: "docType",
        apiParam: "type",
        label: "Document type",
        allLabel: "All types",
        options: served((f) => f.types),
      },
      {
        urlKey: "docRelated",
        apiParam: "relatedTo",
        label: "Related to",
        allLabel: "Everything",
        options: served((f) => f.related),
      },
    ],
    pageKey: "docPage",
    pageSize: 10,
    totalNoun: "documents",
    empty: { title: "No documents on this account yet." },
    load: async ({ filters, search, limit, offset }) => {
      const page = await listAccountDocumentsPage(request, accountId, {
        year: filters.year,
        type: filters.type,
        relatedTo: filters.relatedTo,
        q: search,
        limit,
        offset,
      });
      return {
        items: page.documents,
        total: page.total,
        limit: page.limit,
        offset: page.offset,
      };
    },
  });
}
